import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Zap, 
  Calendar, 
  Share2, 
  BarChart3, 
  ArrowRight,
  Send,
  Twitter,
  Music2,
  Facebook,
  Instagram,
  MessageCircle
} from 'lucide-react';

const Landing = () => {
  const features = [
    {
      icon: <Share2 className="w-6 h-6" />,
      title: "Multi-Platform Publishing",
      description: "Post to Telegram, Twitter, Facebook, Instagram, Discord and more from a single dashboard."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Smart Scheduling",
      description: "Schedule your content for optimal engagement times across all platforms."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Publishing",
      description: "Go live instantly with one click to all your connected accounts."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Content Library",
      description: "Organize drafts, scheduled posts, and published content in one place."
    }
  ];

  const platforms = [
    { name: "Telegram", icon: <Send className="w-8 h-8" />, color: "text-blue-400" },
    { name: "Twitter", icon: <Twitter className="w-8 h-8" />, color: "text-sky-400" },
    { name: "Facebook", icon: <Facebook className="w-8 h-8" />, color: "text-blue-500" },
    { name: "Instagram", icon: <Instagram className="w-8 h-8" />, color: "text-pink-500" },
    { name: "Discord", icon: <MessageCircle className="w-8 h-8" />, color: "text-indigo-400" },
    { name: "TikTok", icon: <Music2 className="w-8 h-8" />, color: "text-pink-400" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">SocialBlast</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="glow-primary" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background glow */}
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] opacity-30"
          style={{
            background: 'radial-gradient(circle at center, rgba(217, 70, 239, 0.3) 0%, transparent 70%)'
          }}
        />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
            <span className="text-sm font-medium text-muted-foreground">Now supporting Telegram, Twitter, Facebook, Instagram & Discord</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6">
            Blast Your Content
            <br />
            <span className="text-gradient">Across All Platforms</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            The command center for content creators. Schedule, publish, and manage your social media 
            presence from one powerful dashboard.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="glow-primary text-lg px-8 py-6" data-testid="hero-get-started-btn">
                Start Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" data-testid="hero-login-btn">
                I have an account
              </Button>
            </Link>
          </div>

          {/* Platform icons */}
          <div className="flex items-center justify-center gap-8 mt-16">
            {platforms.map((platform) => (
              <div 
                key={platform.name}
                className={`flex flex-col items-center gap-2 ${platform.color} opacity-60 hover:opacity-100 transition-opacity`}
              >
                {platform.icon}
                <span className="text-xs font-medium">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A complete toolkit for managing your social media content efficiently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 rounded-xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20" />
            <div className="absolute inset-0 glass" />
            
            <div className="relative p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Ready to Amplify Your Reach?
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                Join content creators who save hours every week with SocialBlast.
              </p>
              <Link to="/register">
                <Button size="lg" className="glow-primary text-lg px-8" data-testid="cta-get-started-btn">
                  Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold">SocialBlast</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Built for creators who mean business.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
